/**
 * Organization product configuration — which sibling Mia Platform products
 * (Homepage, AI Foundry, Console, Flow, P4SaMD, ...) exist for the caller's
 * organization, whether they're enabled, and where they live. Drives the
 * app-switcher (FlowSidebar.jsx, NavigationSidebar.jsx) with a real, backend-configured list
 * instead of a couple of hardcoded entries — same approach as
 * ai-foundry-website's useOrganizationConfiguration/getOrganizationConfiguration
 * (src/hooks/useOrganizationConfiguration.ts, src/services/api.ts).
 *
 * GET /api/bff/organization-configuration (mia-flow-bff proxies it to the authz backend's
 * /api/authz/configuration). Every signed-in person may need it (the app switcher), but the authz backend refuses
 * it (403) for people or organizations it does not know, e.g. on a local kind cluster. A refusal is not an error
 * worth a console line: it is remembered for the tab's session, so a reload does not ask again (and the browser
 * does not log another 403); the app switcher just stays hidden. A new tab asks again.
 */
import { getBffHeaders, buildBffUrl } from '../utils/apiHeaders.js';

export const ORG_CONFIG_REFUSED_SESSION_KEY = 'flow.organizationConfiguration.refused';

/** Error thrown when the configuration cannot be read; `status` is the HTTP status (401/403: refused). */
export class OrganizationConfigurationError extends Error {
  constructor(status) {
    super(`Failed to fetch organization configuration: ${status}`);
    this.name = 'OrganizationConfigurationError';
    this.status = status;
  }
}

const isRefusal = (status) => status === 401 || status === 403;

function rememberedRefusal() {
  try {
    const value = Number(window.sessionStorage.getItem(ORG_CONFIG_REFUSED_SESSION_KEY));
    return isRefusal(value) ? value : null;
  } catch {
    return null;
  }
}

function rememberRefusal(status) {
  try { window.sessionStorage.setItem(ORG_CONFIG_REFUSED_SESSION_KEY, String(status)); } catch { /* no storage */ }
}

// One request per page for every caller (both sidebars ask at mount).
let inflight = null;

/**
 * @returns {Promise<{products: Record<string, {enabled: boolean, title?: string, websiteBaseUrl?: string}>}>}
 *   Rejects with an OrganizationConfigurationError (status 401/403 when refused, without a request when this tab
 *   was already refused).
 */
export function getOrganizationConfiguration() {
  const refused = rememberedRefusal();
  if (refused) { return Promise.reject(new OrganizationConfigurationError(refused)); }
  if (inflight) { return inflight; }
  inflight = (async() => {
    try {
      const response = await fetch(buildBffUrl('/organization-configuration'), {
        method: 'GET',
        headers: getBffHeaders({}, true),
        credentials: 'include',
      });
      if (!response.ok) {
        if (isRefusal(response.status)) { rememberRefusal(response.status); }
        throw new OrganizationConfigurationError(response.status);
      }
      return await response.json();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Tests only: forget the remembered refusal and any request in flight. */
export function resetOrganizationConfigurationCache() {
  inflight = null;
  try { window.sessionStorage.removeItem(ORG_CONFIG_REFUSED_SESSION_KEY); } catch { /* no storage */ }
}

export default { getOrganizationConfiguration };
